/**
 * src/components/landing/RotatingWord.tsx
 * =============================================================================
 * Palabra que va cambiando cada N segundos en el hero de la landing. Se usa
 * para transmitir el rango de "PDFs" que la app sirve: planos, diseños,
 * contratos, presupuestos, etc.
 *
 * El verbo del hero queda fijo ("Sube y corrige"); este componente
 * sustituye al sustantivo. La transición es un slide vertical desde abajo
 * + fade (350ms) — ver `@keyframes ploterWordSwap` en index.css.
 *
 * Ancho reservado: usamos un span inline-block con min-width = la palabra
 * más larga del array para que la línea del hero no "salte" al cambiar.
 *
 * Pausa al hover: si el usuario apunta el cursor encima, paramos el ciclo
 * hasta que sale. Evita que la palabra cambie justo cuando la está leyendo.
 * =============================================================================
 */

import { useEffect, useRef, useState } from 'react'

interface Props {
  /** Lista de sustantivos a rotar. Al menos 2. */
  words: string[]
  /** Milisegundos entre cambios. */
  intervalMs?: number
  /** Clases extra a aplicar al span. */
  className?: string
}

export function RotatingWord({ words, intervalMs = 2200, className }: Props) {
  const [index, setIndex] = useState(0)
  const pausedRef = useRef(false)

  useEffect(() => {
    if (words.length <= 1) return
    const id = window.setInterval(() => {
      if (pausedRef.current) return
      setIndex((i) => (i + 1) % words.length)
    }, intervalMs)
    return () => window.clearInterval(id)
  }, [words.length, intervalMs])

  // Estimamos la palabra más ancha para reservar espacio. No es 100%
  // preciso (depende del kerning), pero suficiente para evitar saltos
  // visibles. Usamos `ch` para que escale con el font-size del padre.
  const widest = words.reduce((a, b) => (b.length > a.length ? b : a), '')

  if (words.length === 0) return null
  const current = words[index] ?? words[0]

  return (
    <span
      // Wrapper inline-block con min-width — fija el espacio antes del
      // contenido animado para que la línea del hero no rebote.
      className={`relative inline-block align-baseline overflow-hidden ${className ?? ''}`}
      style={{ minWidth: `${widest.length}ch` }}
      onMouseEnter={() => { pausedRef.current = true }}
      onMouseLeave={() => { pausedRef.current = false }}
    >
      <span
        // `key={index}` fuerza remount → la animación CSS se reproduce
        // cada vez que cambia la palabra. Sin esto la animación solo
        // correría al mount inicial.
        key={index}
        className="inline-block"
        style={{ animation: 'ploterWordSwap 350ms ease-out' }}
      >
        {current}
      </span>
    </span>
  )
}
